import { Divider, Grid, Paper, Typography } from "@mui/material";
import { batch } from "@tanstack/store";
import { useTranslation } from "react-i18next";
import type { OrganizationAutocompleteItem } from "@/api/Rest/organizations/autocomplete";
import { getOrganizationsAutocomplete } from "@/api/Rest/organizations/autocomplete";
import {
  getFieldErrorMessage,
  useFieldContext,
  useShowFieldError,
} from "@/components/Forms/form.context";
import { GenericAsyncAutocomplete } from "@/components/Forms/genericAsyncAutocomplete";
import type { DeliveryFormType } from "../types";

const REPRESENTATIVE_FIELDS = [
  ["step0.representativeFirstName", "common.form.input.first-name"],
  ["step0.representativeLastName", "common.form.input.last-name"],
  ["step0.representativePhone", "common.form.input.phone"],
  ["step0.representativeEmail", "common.form.input.email"],
  ["step0.representativeCompany", "client.form.company"],
  ["step0.representativeSiret", "client.form.siret"],
] as const;

interface Props {
  form: DeliveryFormType;
  isAdmin: boolean;
}

function fillMandataireFields(
  form: DeliveryFormType,
  org: OrganizationAutocompleteItem | undefined
) {
  batch(() => {
    if (org) {
      form.setFieldValue("step0.representativeFirstName", org.owner?.assigneeFirstName ?? "");
      form.setFieldValue("step0.representativeLastName", org.owner?.assigneeLastName ?? "");
      form.setFieldValue("step0.representativeEmail", org.owner?.assigneeEmail ?? "");
      form.setFieldValue("step0.representativePhone", org.owner?.assigneePhone ?? "");
      form.setFieldValue("step0.representativeCompany", org.name ?? "");
      form.setFieldValue("step0.representativeSiret", org.owner?.siret ?? "");
      form.setFieldValue("step0.selectedOrganizationId", org.slug ?? "");
      if (org.owner?.id) {
        form.setFieldValue("step0.selectedMandataireId", String(org.owner.id));
      }
    } else {
      form.setFieldValue("step0.representativeFirstName", "");
      form.setFieldValue("step0.representativeLastName", "");
      form.setFieldValue("step0.representativeEmail", "");
      form.setFieldValue("step0.representativePhone", "");
      form.setFieldValue("step0.representativeCompany", "");
      form.setFieldValue("step0.representativeSiret", "");
      form.setFieldValue("step0.selectedOrganizationId", "");
      form.setFieldValue("step0.selectedMandataireId", "");
    }
  });
}

function MandataireAutocompleteField({
  form,
  initialLabel,
}: {
  form: DeliveryFormType;
  initialLabel: string | undefined;
}) {
  const field = useFieldContext<string>();
  const errors = field.state.meta.errors;
  const hasError = useShowFieldError(errors);
  const errorMessage = getFieldErrorMessage(errors);

  return (
    <GenericAsyncAutocomplete<OrganizationAutocompleteItem, string>
      queryKeyPrefix="organizations"
      queryFn={(q) => getOrganizationsAutocomplete(q, 20)}
      getOptionLabel={(org) =>
        `${org.owner?.assigneeFirstName ?? ""} ${org.owner?.assigneeLastName ?? ""} (${org.name})`
      }
      getOptionValue={(org) => String(org.owner?.id ?? "")}
      label="Select Mandataire"
      placeholder="Tapez pour rechercher"
      value={field.state.value || null}
      initialOptionLabel={initialLabel}
      onChange={(val, option) => {
        field.handleChange(val ?? "");
        fillMandataireFields(form, option ?? undefined);
      }}
      onBlur={field.handleBlur}
      error={hasError}
      helperText={hasError ? errorMessage : ""}
      noOptionsText={(q) =>
        q.length < 1 ? "Tapez pour rechercher" : "Aucune organisation trouvée"
      }
    />
  );
}

export function MandataireSection({ form, isAdmin }: Props) {
  const { t } = useTranslation();

  const selectedMandataireId = form.state.values.step0.selectedMandataireId;
  const { representativeFirstName, representativeLastName, representativeCompany } =
    form.state.values.step0;

  const initialLabel = selectedMandataireId
    ? `${representativeFirstName} ${representativeLastName} (${representativeCompany})`
    : undefined;

  return (
    <Paper elevation={4} sx={paperSx}>
      <Grid container direction="row" spacing={2} paddingBottom={2}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h5">Mandataire</Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={dividerSx} />
        </Grid>

        {isAdmin && (
          <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
            <form.AppField name="step0.selectedMandataireId">
              {() => <MandataireAutocompleteField form={form} initialLabel={initialLabel} />}
            </form.AppField>
          </Grid>
        )}

        {REPRESENTATIVE_FIELDS.map(([name, label]) => (
          <Grid size={{ md: 6, xs: 12 }} key={name}>
            <form.AppField name={name}>
              {(field) => <field.TextField label={t(label)} disabled />}
            </form.AppField>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

const paperSx = { backgroundColor: "#E7E6F7", borderRadius: 1, padding: "20px", height: "100%" };
const dividerSx = { bgcolor: "#fe5d8d", marginBottom: 5 };
